import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { TransportOrdersForm } from "../../types/transportOrders.ts";
import {
  transportOrderUpdateToExternal,
  transportOrderCreateToExternal,
} from "./convert.ts";
import { useAppDispatch } from "../../redux/store.ts";
import { loadingCellsSlice } from "../../redux/slices/loadingCells.ts";
import { useView } from "../../contexts/ViewContext.tsx";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { extractApiError } from "../../api/errors.ts";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

export const useSetTransportOrderData = () => {
  const showToast = useShowToast();

  const view = useView();
  const [triggerCreate] =
    api.endpoints.createTransportOrderApiTransportOrdersPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateTransportOrderApiTransportOrdersTransportOrderIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteTransportOrderApiTransportOrdersTransportOrderIdDelete.useMutation();

  const dispatch = useAppDispatch();
  const setData = useCallback(
    async (updatedRows: Partial<TransportOrdersForm>[]) => {
      const loadingCellKeys = updatedRows
        .filter((row) => !!row.id)
        .map((row) =>
          Object.entries(row).reduce(
            (acc, [key]) => [...acc, `${view}.${row.id}.${key}`],
            [] as string[],
          ),
        )
        .flat();
      loadingCellKeys.forEach((key) => {
        dispatch(loadingCellsSlice.actions.cellIsPending({ cell: key }));
      });
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = transportOrderUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                transportOrderId: row.id,
                transportOrderUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              transportOrderIn: transportOrderCreateToExternal(
                // TODO: Provide fallback default values to ensure this has full set of values
                row as TransportOrdersForm,
              ),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return [];
        })
        .finally(() => {
          const runningQueries = dispatch(
            api.util.getRunningQueriesThunk(),
          ).filter((query) =>
            query.queryCacheKey.startsWith(
              "listTransportOrdersApiTransportOrdersGet",
            ),
          );
          // Wait for the latest running query for list endpoint to finish, this avoids "flickering"
          void Promise.all(runningQueries).finally(() => {
            loadingCellKeys.forEach((key) => {
              dispatch(loadingCellsSlice.actions.cellIsLoaded({ cell: key }));
            });
          });
        });
    },
    [triggerCreate, triggerUpdate, dispatch, view, showToast],
  );

  const deleteData = useCallback(
    async (transportOrderId: number) => {
      try {
        const result = await triggerDelete({ transportOrderId });
        console.info("Deleted forwarding order", transportOrderId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
